/**
 * 菜单定义 —— 平台运营端与租户端共用一套框架，靠 principalType + 权限码过滤。
 *
 * 设计原则：菜单项的 permission 与后端接口鉴权用的是同一批权限码，
 * 前端.filter 只是体验优化，越权请求仍会被后端拒绝。
 */

import {
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  ClusterOutlined,
  CloudUploadOutlined,
  ContainerOutlined,
  DashboardOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  IdcardOutlined,
  NodeIndexOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  UserOutlined,
  ApiOutlined,
  ReconciliationOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
  FileAddOutlined,
  FundOutlined,
  BarChartOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import { PermissionCode } from '@ilc/shared';

export interface MenuNode {
  key: string;
  label: string;
  icon?: React.ReactNode;
  /** 叶子节点路径；分组节点无 path */
  path?: string;
  permission?: string;
  /** 仅平台运营端可见 */
  platformOnly?: boolean;
  /** 仅租户端可见 */
  tenantOnly?: boolean;
  /** 该菜单对应的角色职责说明，鼠标悬浮提示用 */
  hint?: string;
  children?: MenuNode[];
}

/** 租户端菜单 */
export const TENANT_MENUS: MenuNode[] = [
  {
    key: '/dashboard',
    label: '工作台',
    icon: <DashboardOutlined />,
    path: '/dashboard',
    tenantOnly: true,
  },
  {
    key: 'g-invoice',
    label: '发票业务',
    icon: <FileTextOutlined />,
    tenantOnly: true,
    children: [
      {
        key: '/business/invoices',
        label: '我的发票',
        icon: <FileSearchOutlined />,
        path: '/business/invoices',
        permission: PermissionCode.INVOICE_READ,
        hint: '项目部经办人：上传发票、关联合同、处理退回',
      },
      {
        key: '/business/upload',
        label: '上传发票',
        icon: <CloudUploadOutlined />,
        path: '/business/upload',
        permission: PermissionCode.INVOICE_CREATE,
        hint: '支持批量与拖拽上传，按票种提示查验要素',
      },
      {
        key: '/business/review',
        label: '财务初审',
        icon: <AuditOutlined />,
        path: '/business/review',
        permission: PermissionCode.INVOICE_REVIEW,
        hint: '财务初审岗：审阅校验报告、人工复核、确认推送',
      },
      {
        key: '/business/bookkeeping',
        label: '记账同步',
        icon: <ReconciliationOutlined />,
        path: '/business/bookkeeping',
        permission: PermissionCode.TPLUS_READ,
        hint: '财务记账岗：查看已推送 T+ 的发票与凭证回写状态',
      },
      {
        key: '/business/legal',
        label: '合同与抽查',
        icon: <SafetyCertificateOutlined />,
        path: '/business/legal',
        permission: PermissionCode.CONTRACT_READ,
        hint: '法务岗：合同管理、归档检索、事后抽查',
      },
      {
        key: '/business/archive',
        label: '档案库',
        icon: <FolderOpenOutlined />,
        path: '/business/archive',
        permission: PermissionCode.ARCHIVE_READ,
      },
    ],
  },

  /* —— 待办与审批：跨业务类型的入口，放在业务组之间 —— */
  {
    key: '/business/approvals',
    label: '待办与审批',
    icon: <CheckSquareOutlined />,
    path: '/business/approvals',
    permission: PermissionCode.INVOICE_READ,
    tenantOnly: true,
    hint: '合同、费用申请、报销单与发票的待办集中在这里；按角色匹配，不是按人',
  },

  /* —— 合同全生命周期（延伸需求第一部分）—— */
  {
    key: 'g-contract',
    label: '合同管理',
    icon: <ContainerOutlined />,
    tenantOnly: true,
    children: [
      {
        key: '/business/contracts',
        label: '合同台账',
        icon: <ContainerOutlined />,
        path: '/business/contracts',
        permission: PermissionCode.CONTRACT_READ,
        hint: '经营部 / 财务 / 法务：总包、分包、采购、租赁、服务合同与补充协议',
      },
      {
        key: '/business/contract-alerts',
        label: '合同预警',
        icon: <WarningOutlined />,
        path: '/business/contract-alerts',
        permission: PermissionCode.CONTRACT_READ,
        hint: '超开票、超付款、临近到期、阶段超比例等八类预警',
      },
    ],
  },

  /* —— 费用报销（延伸需求第二部分）—— */
  {
    key: 'g-expense',
    label: '费用报销',
    icon: <ReconciliationOutlined />,
    tenantOnly: true,
    children: [
      {
        key: '/business/expenses/apply',
        label: '费用申请',
        icon: <FileAddOutlined />,
        path: '/business/expenses/apply',
        permission: PermissionCode.EXPENSE_APPLY,
        hint: '全员：事前申请，先说清要花什么钱',
      },
      {
        key: '/business/expenses/reimburse',
        label: '报销单',
        icon: <ReconciliationOutlined />,
        path: '/business/expenses/reimburse',
        permission: PermissionCode.EXPENSE_READ,
        hint: '全员：填明细、传发票、走审批',
      },
      {
        key: '/business/expenses/review',
        label: '报销审核',
        icon: <AuditOutlined />,
        path: '/business/expenses/review',
        permission: PermissionCode.EXPENSE_REVIEW,
        hint: '财务初审岗：审发票校验结果、金额与预算',
      },
      {
        key: '/business/expenses/pay',
        label: '出纳付款',
        icon: <BankOutlined />,
        path: '/business/expenses/pay',
        permission: PermissionCode.EXPENSE_PAY,
        hint: '出纳：按核定的收款账户执行付款并登记',
      },
      {
        key: '/business/expenses/budget',
        label: '预算管理',
        icon: <FundOutlined />,
        path: '/business/expenses/budget',
        permission: PermissionCode.EXPENSE_BUDGET_MANAGE,
        hint: '财务 / 管理员：按期间 × 部门 × 费用类型编制与调整',
      },
      {
        key: '/business/expenses/reports',
        label: '费用报表',
        icon: <BarChartOutlined />,
        path: '/business/expenses/reports',
        permission: PermissionCode.EXPENSE_REPORT,
        hint: '按人 / 部门 / 类型 / 月份 / 工程 / 预算六个维度',
      },
      {
        key: '/business/expenses/alerts',
        label: '费用预警',
        icon: <WarningOutlined />,
        path: '/business/expenses/alerts',
        permission: PermissionCode.EXPENSE_READ,
        hint: '预算接近上限 / 已超支 / 单据长期未处理',
      },
    ],
  },

  {
    key: 'g-admin',
    label: '系统管理',
    icon: <SettingOutlined />,
    tenantOnly: true,
    children: [
      {
        key: '/admin/users',
        label: '用户管理',
        icon: <UserOutlined />,
        path: '/admin/users',
        permission: PermissionCode.USER_READ,
      },
      {
        key: '/admin/roles',
        label: '角色权限',
        icon: <TeamOutlined />,
        path: '/admin/roles',
        permission: PermissionCode.ROLE_READ,
      },
      {
        key: '/admin/depts',
        label: '部门管理',
        icon: <ClusterOutlined />,
        path: '/admin/depts',
        permission: PermissionCode.DEPT_READ,
      },
      {
        key: '/admin/flow-templates',
        label: '流程模板',
        icon: <NodeIndexOutlined />,
        path: '/admin/flow-templates',
        permission: PermissionCode.FLOW_TEMPLATE_READ,
      },
      {
        key: '/admin/tenant-config',
        label: '租户配置',
        icon: <AppstoreOutlined />,
        path: '/admin/tenant-config',
        permission: PermissionCode.TENANT_CONFIG_READ,
      },
      {
        key: '/admin/providers',
        label: '外部接口配置',
        icon: <ApiOutlined />,
        path: '/admin/providers',
        permission: PermissionCode.PROVIDER_CONFIG_READ,
      },
      {
        key: '/admin/provider-logs',
        label: '接口调用日志',
        icon: <ContainerOutlined />,
        path: '/admin/provider-logs',
        permission: PermissionCode.PROVIDER_LOG_READ,
      },
    ],
  },
];

/** 平台运营端菜单 */
export const PLATFORM_MENUS: MenuNode[] = [
  {
    key: '/platform/metrics',
    label: '运营概览',
    icon: <DashboardOutlined />,
    path: '/platform/metrics',
    platformOnly: true,
  },
  {
    key: '/platform/tenants',
    label: '租户管理',
    icon: <ShopOutlined />,
    path: '/platform/tenants',
    platformOnly: true,
    hint: '租户审核认证、停用、调套餐、配额',
  },
  {
    key: '/platform/plans',
    label: '套餐管理',
    icon: <BankOutlined />,
    path: '/platform/plans',
    platformOnly: true,
  },
  {
    key: '/platform/audit-logs',
    label: '审计日志',
    icon: <IdcardOutlined />,
    path: '/platform/audit-logs',
    platformOnly: true,
  },
];

/** 所有菜单（含分组）拍平为叶子节点 */
export function flattenMenus(nodes: MenuNode[]): MenuNode[] {
  const out: MenuNode[] = [];
  for (const node of nodes) {
    if (node.children?.length) out.push(...flattenMenus(node.children));
    else out.push(node);
  }
  return out;
}

/** 面包屑：路径 → 祖先链 */
export function findMenuTrail(nodes: MenuNode[], pathname: string): MenuNode[] {
  for (const node of nodes) {
    if (node.path && pathname.startsWith(node.path)) return [node];
    if (node.children?.length) {
      const child = findMenuTrail(node.children, pathname);
      if (child.length) return [node, ...child];
    }
  }
  return [];
}
